import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasDashboardFullAccess } from '../../utils/workspaces';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import Select from 'react-select';
import {
  MdSearch,
  MdAdd,
  MdClose,
  MdPerson,
  MdHistory,
  MdExpandMore,
  MdExpandLess,
  MdSchool,
  MdCalendarToday,
  MdPhone,
  MdInfo,
  MdCheckCircle,
  MdWarning,
  MdRefresh,
  MdComment,
  MdFiberManualRecord,
  MdDelete,
} from 'react-icons/md';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_META = {
  pending: {
    label: 'Pending',
    dot: 'bg-amber-400',
    badge: 'border border-amber-200 bg-amber-50 text-amber-700',
  },
  'in followup': {
    label: 'In Follow-up',
    dot: 'bg-sky-400',
    badge: 'border border-sky-200 bg-sky-50 text-sky-700',
  },
  'planned to rejoin': {
    label: 'Planned to Rejoin',
    dot: 'bg-violet-400',
    badge: 'border border-violet-200 bg-violet-50 text-violet-700',
  },
  reactivated: {
    label: 'Reactivated',
    dot: 'bg-emerald-400',
    badge: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  'permanently inactive': {
    label: 'Permanently Inactive',
    dot: 'bg-red-400',
    badge: 'border border-red-200 bg-red-50 text-red-700',
  },
  transferred: {
    label: 'Transferred',
    dot: 'bg-indigo-400',
    badge: 'border border-indigo-200 bg-indigo-50 text-indigo-700',
  },
  'no answer': {
    label: 'No Answer',
    dot: 'bg-slate-400',
    badge: 'border border-slate-200 bg-slate-100 text-slate-600',
  },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, { label }]) => ({
  value,
  label,
}));

const GOAL_OPTIONS = [
  { value: 'reactivate', label: 'Reactivate' },
  { value: 'confirm inactive', label: 'Confirm Inactive' },
  { value: 'transfer to another branch', label: 'Transfer to Another Branch' },
  { value: 'transfer to another course', label: 'Transfer to Another Course' },
  { value: 'other', label: 'Other' },
];

const FILTER_STATUS_OPTIONS = [{ value: '', label: 'All Statuses' }, ...STATUS_OPTIONS];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initialsFromName(name) {
  if (!name) return 'NA';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ src, name, gender, size = 40 }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = initialsFromName(name);
  const isMale = gender && ['male', 'm'].includes(String(gender).toLowerCase());
  const bg = isMale ? '#3b82f6' : '#ec4899';

  if (src && !imgErr) {
    return (
      <img
        src={getImageURL(src)}
        alt={name}
        onError={() => setImgErr(true)}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold select-none"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, badge: 'border border-gray-200 bg-gray-50 text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${meta.badge}`}>
      <MdFiberManualRecord className={`text-[8px] ${meta.dot.replace('bg-', 'text-')}`} />
      {meta.label}
    </span>
  );
}

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return val;
  }
}

function fmtDatetime(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return val;
  }
}

// Returns YYYY-MM-DD in the browser's local timezone (avoids UTC shift from server-serialised Date objects)
function toLocalDateStr(val) {
  if (!val) return null;
  const d = new Date(val);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Add Inquiry Modal ────────────────────────────────────────────────────────

const REG_STATUS_BADGE = {
  active: 'bg-green-50 border-green-200 text-green-700',
  postponed: 'bg-amber-50 border-amber-200 text-amber-700',
  pending: 'bg-slate-100 border-slate-200 text-slate-600',
  suspending: 'bg-orange-50 border-orange-200 text-orange-700',
  inactivating: 'bg-red-50 border-red-200 text-red-600',
  inactivated: 'bg-red-100 border-red-300 text-red-700',
  suspended: 'bg-gray-100 border-gray-200 text-gray-600',
};

function AddInquiryModal({ onClose, onAdded }) {
  // step 1 = search, step 2 = confirm inactivation (if not already), step 3 = fill form
  const [step, setStep] = useState(1);
  const [regInput, setRegInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [saving, setSaving] = useState(false);

  const _user = AuthService.getUser();
  const _isSuperAdmin = hasDashboardFullAccess(_user);
  const _userRole = _user?.superAdmin?.staff?.role || _user?.superAdmin?.role;
  const isAdmin = _isSuperAdmin || _userRole === 'Admin' || _userRole === 'Administrator';

  const [form, setForm] = useState({
    inquiry_goal: 'reactivate',
    assigned_to: null,
  });
  const [staffOptions, setStaffOptions] = useState([]);

  useEffect(() => {
    axiosInstance
      .get('/admin/staff')
      .then((r) => {
        const list = Array.isArray(r.data?.staff) ? r.data.staff : [];
        const active = list.filter((s) => s.loginStatus === 'active');
        setStaffOptions(active.map((s) => ({
          value: s.staffId,
          label: `${s.name}${s.position ? ` (${s.position})` : ''}`,
        })));
      })
      .catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!regInput.trim()) return;
    setSearching(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const { data } = await axiosInstance.get('/admin/inactivated-inquiry/search', {
        params: { registrationNumber: regInput.trim(), allCourses: isAdmin ? '1' : '0', anyStatus: '1' },
      });
      if (!data || !data.studentId) {
        setSearchError('No student found with that registration number.');
      } else {
        setSearchResult(data);
        // If already inactivated skip the confirmation step
        setStep(data.registrationStatus === 'inactivated' ? 3 : 2);
      }
    } catch (err) {
      setSearchError(err?.response?.data?.message || 'No student found with that registration number.');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!searchResult) return;
    setSaving(true);
    try {
      await axiosInstance.post('/admin/inactivated-inquiry', {
        student_id: searchResult.studentId,
        registration_id: searchResult.registrationId,
        inquiry_goal: form.inquiry_goal,
        assigned_to: form.assigned_to?.value || null,
        force_inactivate: searchResult.registrationStatus !== 'inactivated',
      });
      onAdded();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create inquiry.');
    } finally {
      setSaving(false);
    }
  };

  const stepTitle =
    step === 1 ? 'Search by registration number'
    : step === 2 ? 'Confirm inactivation'
    : 'Review student & complete the inquiry';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">New Inactivated Inquiry</h2>
            <p className="text-xs text-gray-500 mt-0.5">{stepTitle}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <MdClose size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* ── Step 1 – Search ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3">
                <MdInfo className="text-amber-500 mt-0.5 flex-shrink-0" size={18} />
                {isAdmin ? (
                  <p className="text-xs text-amber-700 leading-relaxed">
                    As an <strong>admin</strong>, you can search any student across all courses.
                  </p>
                ) : (
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Search students from your coordinated courses by registration number.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Registration Number</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={regInput}
                    onChange={(e) => setRegInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="e.g. HND/2024/001"
                    className="flex-1 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={searching || !regInput.trim()}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
                  >
                    {searching ? (
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : (
                      <MdSearch size={18} />
                    )}
                    Search
                  </button>
                </div>
                {searchError && (
                  <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                    <MdWarning size={14} /> {searchError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Step 2 – Confirm inactivation ── */}
          {step === 2 && searchResult && (
            <div className="space-y-4">
              {/* Student card */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-100 rounded-xl">
                <Avatar src={searchResult.imagePath} name={searchResult.fullName} gender={searchResult.gender} size={52} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm truncate">{searchResult.fullName}</p>
                  <p className="text-xs text-indigo-600 font-medium">{searchResult.registrationNumber}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{searchResult.courseName} · {searchResult.batchName}</p>
                  <span className={`inline-flex items-center mt-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full border capitalize ${REG_STATUS_BADGE[searchResult.registrationStatus] || 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                    Current: {searchResult.registrationStatus}
                  </span>
                </div>
                <button
                  onClick={() => { setStep(1); setSearchResult(null); }}
                  className="text-xs text-gray-400 hover:text-indigo-600 underline self-start flex-shrink-0"
                >
                  Change
                </button>
              </div>

              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                <MdWarning className="text-red-500 mt-0.5 flex-shrink-0" size={20} />
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-red-700">Confirm Inactivation</p>
                  <p className="text-xs text-red-600 leading-relaxed">
                    This student's registration is currently{' '}
                    <strong className="capitalize">{searchResult.registrationStatus}</strong>.
                    Creating this inquiry will automatically update the registration status to{' '}
                    <strong>Inactivated</strong>. This change will be recorded.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3 – Fill inquiry form ── */}
          {step === 3 && searchResult && (
            <div className="space-y-5">
              {/* Student card */}
              <div className="flex items-center gap-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                <Avatar src={searchResult.imagePath} name={searchResult.fullName} gender={searchResult.gender} size={52} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm truncate">{searchResult.fullName}</p>
                  <p className="text-xs text-indigo-600 font-medium">{searchResult.registrationNumber}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{searchResult.courseName} · {searchResult.batchName}</p>
                </div>
                <button
                  onClick={() => { setStep(1); setSearchResult(null); }}
                  className="text-xs text-gray-400 hover:text-indigo-600 underline flex-shrink-0"
                >
                  Change
                </button>
              </div>

              {/* Inquiry Goal */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Inquiry Goal</label>
                <Select
                  options={GOAL_OPTIONS}
                  value={GOAL_OPTIONS.find((o) => o.value === form.inquiry_goal) || null}
                  onChange={(o) => setForm((p) => ({ ...p, inquiry_goal: o?.value || 'reactivate' }))}
                  classNamePrefix="rs"
                  styles={rsStyles()}
                />
              </div>

              {/* Assigned To */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Assign To (Optional)</label>
                <Select
                  isClearable
                  options={staffOptions}
                  value={form.assigned_to}
                  onChange={(o) => setForm((p) => ({ ...p, assigned_to: o }))}
                  placeholder="Select active staff member..."
                  classNamePrefix="rs"
                  styles={rsStyles()}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Footer – Step 2 confirmation buttons ── */}
        {step === 2 && (
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
            <button
              onClick={() => { setStep(1); setSearchResult(null); }}
              className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors"
            >
              <MdCheckCircle size={16} /> Yes, Mark as Inactivated &amp; Continue
            </button>
          </div>
        )}

        {/* ── Footer – Step 3 save button ── */}
        {step === 3 && (
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
            <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
            >
              {saving && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              Create Inquiry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Followup Modal ───────────────────────────────────────────────────────

function AddFollowupModal({ inquiry, onClose, onAdded }) {
  const [form, setForm] = useState({
    comment: '',
    comment_date: new Date().toISOString().slice(0, 10),
    next_reminder_date: '',
    next_reminder_comment: '',
    status_update: '',
    planned_rejoin_date: '',
    is_attending_classes: '',
    attending_since: '',
  });
  const [saving, setSaving] = useState(false);

  const showPlannedDate = form.status_update === 'planned to rejoin';
  const showAttendingSince = form.is_attending_classes === '1';

  const handleSave = async () => {
    if (!form.comment.trim()) {
      alert('Please enter a comment.');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post(`/admin/inactivated-inquiry/${inquiry.inquiry_id}/followup`, {
        comment: form.comment,
        comment_date: form.comment_date || null,
        next_reminder_date: form.next_reminder_date || null,
        next_reminder_comment: form.next_reminder_comment || null,
        status_update: form.status_update || null,
        planned_rejoin_date: showPlannedDate ? (form.planned_rejoin_date || null) : null,
        is_attending_classes: form.is_attending_classes !== '' ? Number(form.is_attending_classes) : null,
        attending_since: showAttendingSince ? (form.attending_since || null) : null,
      });
      onAdded();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to save follow-up.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Add Follow-up</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{inquiry.full_name} · {inquiry.registration_number}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <MdClose size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Comment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={4}
              value={form.comment}
              onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
              placeholder="What was discussed / outcome of the follow-up..."
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Comment Date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Comment Date</label>
              <input
                type="date"
                value={form.comment_date}
                onChange={(e) => setForm((p) => ({ ...p, comment_date: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              />
            </div>
            {/* Next Reminder */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Next Reminder Date</label>
              <input
                type="date"
                value={form.next_reminder_date}
                onChange={(e) => setForm((p) => ({ ...p, next_reminder_date: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Next Reminder Comment */}
          {form.next_reminder_date && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Next Reminder Note</label>
              <input
                type="text"
                value={form.next_reminder_comment}
                onChange={(e) => setForm((p) => ({ ...p, next_reminder_comment: e.target.value }))}
                placeholder="What to follow up on..."
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              />
            </div>
          )}

          {/* Status Update */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Update Status (Optional)</label>
            <Select
              isClearable
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === form.status_update) || null}
              onChange={(o) => setForm((p) => ({ ...p, status_update: o?.value || '' }))}
              placeholder="Leave unchanged..."
              classNamePrefix="rs"
              styles={rsStyles()}
            />
          </div>

          {/* Planned Rejoin Date — shown only when status = planned to rejoin */}
          {showPlannedDate && (
            <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 space-y-3">
              <p className="text-xs font-medium text-violet-700 flex items-center gap-1.5">
                <MdCalendarToday size={14} /> Student plans to rejoin
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Planned Rejoin Date</label>
                <input
                  type="date"
                  value={form.planned_rejoin_date}
                  onChange={(e) => setForm((p) => ({ ...p, planned_rejoin_date: e.target.value }))}
                  className="w-full border border-violet-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent bg-white"
                />
              </div>
            </div>
          )}

          {/* Attending Classes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Is Student Attending Classes?</label>
            <div className="flex gap-3">
              {[{ val: '', lbl: 'No Change' }, { val: '1', lbl: 'Yes, Attending' }, { val: '0', lbl: 'Not Attending' }].map(({ val, lbl }) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, is_attending_classes: val, attending_since: val === '1' ? p.attending_since : '' }))}
                  className={`flex-1 py-2 text-xs font-medium rounded-xl border transition-colors ${
                    form.is_attending_classes === val
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {showAttendingSince && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Attending Since</label>
              <input
                type="date"
                value={form.attending_since}
                onChange={(e) => setForm((p) => ({ ...p, attending_since: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
          >
            {saving && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            Save Follow-up
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inquiry Card ─────────────────────────────────────────────────────────────

function InquiryCard({ inquiry, onFollowupAdded, onDeleted, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Inquiry?',
      html: `Delete inquiry for <strong>${inquiry.full_name}</strong>?<br/>This will also remove all follow-up records and cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/inactivated-inquiry/${inquiry.inquiry_id}`);
      onDeleted();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: err?.response?.data?.message || 'Failed to delete inquiry.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const loadFollowups = useCallback(async () => {
    setLoadingFollowups(true);
    try {
      const { data } = await axiosInstance.get(`/admin/inactivated-inquiry/${inquiry.inquiry_id}/followups`);
      setFollowups(Array.isArray(data) ? data : []);
    } catch {
      setFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  }, [inquiry.inquiry_id]);

  const handleExpand = () => {
    if (!expanded) loadFollowups();
    setExpanded((v) => !v);
  };

  const handleFollowupAdded = () => {
    loadFollowups();
    onFollowupAdded();
  };

  const isReactivated = inquiry.status === 'reactivated';

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${isReactivated ? 'border-emerald-200' : 'border-gray-100'}`}>
      {/* Card Header */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <Avatar src={inquiry.image_path} name={inquiry.full_name} gender={inquiry.gender} size={48} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{inquiry.full_name}</span>
              <StatusBadge status={inquiry.status} />
              {inquiry.is_attending_classes === 1 && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 font-medium">
                  <MdSchool size={11} /> Attending
                </span>
              )}
            </div>
            <p className="text-xs text-indigo-600 font-medium mt-0.5">{inquiry.registration_number}</p>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{inquiry.course_name} · {inquiry.batch_name}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowFollowupModal(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
            >
              <MdAdd size={14} /> Follow-up
            </button>
            {isAdmin && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 disabled:opacity-40 transition-colors"
                title="Delete inquiry (admin only)"
              >
                {deleting
                  ? <span className="w-4 h-4 border-2 border-red-300 border-t-red-500 rounded-full animate-spin block" />
                  : <MdDelete size={18} />}
              </button>
            )}
            <button
              onClick={handleExpand}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title={expanded ? 'Collapse' : 'View history'}
            >
              {expanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-500">
          {inquiry.assigned_to_name && (
            <span className="flex items-center gap-1">
              <MdPerson size={13} className="text-gray-400" />
              Assigned: <span className="text-gray-700 font-medium">{inquiry.assigned_to_name}</span>
            </span>
          )}
          {inquiry.next_reminder_date && (
            <span className="flex items-center gap-1">
              <MdCalendarToday size={13} className="text-gray-400" />
              Reminder: <span className="text-gray-700 font-medium">{fmtDate(inquiry.next_reminder_date)}</span>
            </span>
          )}
          {inquiry.planned_rejoin_date && (
            <span className="flex items-center gap-1">
              <MdCheckCircle size={13} className="text-violet-400" />
              <span className="text-violet-600 font-medium">Plans to rejoin {fmtDate(inquiry.planned_rejoin_date)}</span>
            </span>
          )}
          {inquiry.reactivated_date && (
            <span className="flex items-center gap-1">
              <MdCheckCircle size={13} className="text-emerald-500" />
              <span className="text-emerald-600 font-medium">Reactivated {fmtDate(inquiry.reactivated_date)}</span>
            </span>
          )}
          {inquiry.inquiry_goal && (
            <span className="flex items-center gap-1">
              Goal: <span className="text-gray-700 capitalize">{inquiry.inquiry_goal}</span>
            </span>
          )}
          <span className="flex items-center gap-1">
            <MdPhone size={13} className="text-gray-400" />
            {inquiry.telephone || '—'}
          </span>
        </div>

        {inquiry.inactivation_reason && (
          <p className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 leading-relaxed">
            <span className="font-medium text-gray-500">Reason: </span>{inquiry.inactivation_reason}
          </p>
        )}
      </div>

      {/* Followups Panel */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <MdHistory size={15} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Follow-up History</span>
          </div>

          {loadingFollowups && (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
              <span className="w-4 h-4 border-2 border-gray-200 border-t-indigo-400 rounded-full animate-spin" />
              Loading...
            </div>
          )}

          {!loadingFollowups && followups.length === 0 && (
            <p className="text-xs text-gray-400 italic py-1">No follow-ups recorded yet.</p>
          )}

          {!loadingFollowups && followups.length > 0 && (
            <div className="space-y-3">
              {followups.map((fu, idx) => (
                <div key={fu.followup_id} className="relative pl-5">
                  {/* Timeline line */}
                  {idx < followups.length - 1 && (
                    <div className="absolute left-1.5 top-5 bottom-0 w-px bg-gray-200" />
                  )}
                  <div className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 border-indigo-300 bg-white" />
                  <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700">{fu.created_by_name || 'Staff'}</span>
                        {fu.status_update && <StatusBadge status={fu.status_update} />}
                      </div>
                      <span className="text-xs text-gray-400">{fmtDatetime(fu.created_date)}</span>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed">{fu.comment}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      {fu.planned_rejoin_date && (
                        <span className="text-violet-600 font-medium flex items-center gap-1">
                          <MdCalendarToday size={11} /> Rejoin: {fmtDate(fu.planned_rejoin_date)}
                        </span>
                      )}
                      {fu.next_reminder_date && (
                        <span className="flex items-center gap-1">
                          <MdCalendarToday size={11} /> Next: {fmtDate(fu.next_reminder_date)}
                        </span>
                      )}
                      {fu.is_attending_classes === 1 && (
                        <span className="text-teal-600 flex items-center gap-1">
                          <MdSchool size={11} /> Attending since {fmtDate(fu.attending_since)}
                        </span>
                      )}
                      {fu.is_attending_classes === 0 && (
                        <span className="text-orange-500 flex items-center gap-1">
                          <MdWarning size={11} /> Stopped attending
                        </span>
                      )}
                      {fu.next_reminder_comment && (
                        <span className="flex items-center gap-1">
                          <MdComment size={11} /> {fu.next_reminder_comment}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showFollowupModal && (
        <AddFollowupModal
          inquiry={inquiry}
          onClose={() => setShowFollowupModal(false)}
          onAdded={handleFollowupAdded}
        />
      )}
    </div>
  );
}

// ─── react-select shared styles ───────────────────────────────────────────────

function rsStyles() {
  return {
    control: (b, s) => ({
      ...b,
      borderRadius: 12,
      borderColor: s.isFocused ? '#818cf8' : '#e5e7eb',
      boxShadow: s.isFocused ? '0 0 0 2px #c7d2fe' : 'none',
      minHeight: 42,
      fontSize: 14,
      '&:hover': { borderColor: '#818cf8' },
    }),
    menu: (b) => ({ ...b, borderRadius: 12, zIndex: 9999 }),
    option: (b, s) => ({
      ...b,
      fontSize: 14,
      backgroundColor: s.isSelected ? '#4f46e5' : s.isFocused ? '#eef2ff' : 'white',
      color: s.isSelected ? 'white' : '#374151',
    }),
  };
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InactivatedInquiry() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // filters
  const [searchQ, setSearchQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [reminderTodayOnly, setReminderTodayOnly] = useState(false);

  // stats
  const _td = new Date();
  const todayStr = `${_td.getFullYear()}-${String(_td.getMonth() + 1).padStart(2, '0')}-${String(_td.getDate()).padStart(2, '0')}`;
  const stats = useMemo(() => {
    const total = inquiries.length;
    const pending = inquiries.filter((i) => i.status === 'pending').length;
    const inFollowup = inquiries.filter((i) => i.status === 'in followup').length;
    const plannedRejoin = inquiries.filter((i) => i.status === 'planned to rejoin').length;
    const reactivated = inquiries.filter((i) => i.status === 'reactivated').length;
    const attending = inquiries.filter((i) => i.is_attending_classes === 1).length;
    const reminderToday = inquiries.filter(
      (i) => i.next_reminder_date && toLocalDateStr(i.next_reminder_date) === todayStr
    ).length;
    return { total, pending, inFollowup, plannedRejoin, reactivated, attending, reminderToday };
  }, [inquiries, todayStr]);

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/inactivated-inquiry');
      setInquiries(Array.isArray(data) ? data : []);
    } catch {
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const assigneeOptions = useMemo(() => {
    const seen = new Map();
    inquiries.forEach((i) => {
      if (i.assigned_to && !seen.has(i.assigned_to)) {
        seen.set(i.assigned_to, i.assigned_to_name || `Staff #${i.assigned_to}`);
      }
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value: String(value), label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [inquiries]);

  const filtered = useMemo(() => {
    let list = inquiries;
    if (filterStatus) list = list.filter((i) => i.status === filterStatus);
    if (filterAssignee) list = list.filter((i) => String(i.assigned_to) === filterAssignee);
    if (reminderTodayOnly)
      list = list.filter(
        (i) => i.next_reminder_date && toLocalDateStr(i.next_reminder_date) === todayStr
      );
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      list = list.filter(
        (i) =>
          String(i.full_name || '').toLowerCase().includes(q) ||
          String(i.registration_number || '').toLowerCase().includes(q) ||
          String(i.course_name || '').toLowerCase().includes(q) ||
          String(i.batch_name || '').toLowerCase().includes(q) ||
          String(i.telephone || '').includes(q)
      );
    }
    return list;
  }, [inquiries, filterStatus, filterAssignee, reminderTodayOnly, searchQ, todayStr]);

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50/50">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-100 px-6 py-5">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Inactivated Student Inquiry</h1>
              <p className="text-sm text-gray-500 mt-0.5">Track and follow up on inactivated students from your coordinated courses</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadInquiries}
                className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors"
                title="Refresh"
              >
                <MdRefresh size={18} />
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
              >
                <MdAdd size={18} /> Add Inquiry
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
          {/* Stats Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Total', value: stats.total, color: 'text-gray-900', bg: 'bg-white' },
              { label: 'Pending', value: stats.pending, color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'In Follow-up', value: stats.inFollowup, color: 'text-sky-600', bg: 'bg-sky-50' },
              { label: 'Plan to Rejoin', value: stats.plannedRejoin, color: 'text-violet-600', bg: 'bg-violet-50' },
              { label: 'Reactivated', value: stats.reactivated, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Still Attending', value: stats.attending, color: 'text-teal-600', bg: 'bg-teal-50' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`${bg} border border-gray-100 rounded-xl px-4 py-3 text-center shadow-sm`}>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search by name, registration, course, batch, phone..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white"
              />
            </div>
            <div className="w-full sm:w-52">
              <Select
                isClearable
                options={FILTER_STATUS_OPTIONS}
                value={FILTER_STATUS_OPTIONS.find((o) => o.value === filterStatus) || null}
                onChange={(o) => setFilterStatus(o?.value || '')}
                placeholder="Filter by status..."
                classNamePrefix="rs"
                styles={rsStyles()}
              />
            </div>
            <div className="w-full sm:w-52">
              <Select
                isClearable
                options={assigneeOptions}
                value={assigneeOptions.find((o) => o.value === filterAssignee) || null}
                onChange={(o) => setFilterAssignee(o?.value || '')}
                placeholder="Filter by assigned..."
                classNamePrefix="rs"
                styles={rsStyles()}
              />
            </div>
            <button
              onClick={() => setReminderTodayOnly((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors flex-shrink-0 ${
                reminderTodayOnly
                  ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 bg-white'
              }`}
              title="Show only students with a reminder due today"
            >
              <MdCalendarToday size={16} />
              Reminder Today
              {stats.reminderToday > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  reminderTodayOnly ? 'bg-white text-orange-600' : 'bg-orange-100 text-orange-600'
                }`}>
                  {stats.reminderToday}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
              <span className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
              <span className="text-sm">Loading inquiries...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                <MdPerson size={32} className="text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">
                {inquiries.length === 0 ? 'No inquiries yet' : 'No results match your filters'}
              </p>
              {inquiries.length === 0 && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  + Add your first inquiry
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-700">{filtered.length}</span> {filtered.length === 1 ? 'inquiry' : 'inquiries'}
              </p>
              {filtered.map((inquiry) => {
                const _u = AuthService.getUser();
                const _role = _u?.superAdmin?.staff?.role || _u?.superAdmin?.role;
                const _isAdmin = hasDashboardFullAccess(_u) || _role === 'Admin' || _role === 'Administrator';
                return (
                  <InquiryCard
                    key={inquiry.inquiry_id}
                    inquiry={inquiry}
                    isAdmin={_isAdmin}
                    onFollowupAdded={loadInquiries}
                    onDeleted={loadInquiries}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <AddInquiryModal
          onClose={() => setShowAddModal(false)}
          onAdded={loadInquiries}
        />
      )}
    </DashboardLayout>
  );
}
